import type { QueryKey, QueryStatus } from '@tanstack/react-query';

import { isDependencyCacheValid } from '../../../helpers/revisions';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import { useSelectedReleaseId } from '../../../redux/project_grouping/hooks';
import type { ProjectGroupRelease } from '../../../types';

import { getReleaseGroupBaseQueryKey } from './useReleaseGroup';

//  How often to refetch a release whose projects are still having their dependencies determined.
export const RELEASE_DEPENDENCY_POLL_INTERVAL_MS = 7000;

//  Many components on the release group pages read the selected release; without a stale time, each one
//  mounting would refetch it. Changes made in the UI invalidate it, and dependency polling ignores this.
export const RELEASE_STALE_TIME_MS = 60 * 1000;

export const getReleaseGroupReleaseQueryKey = (releaseGroupId: number, releaseId: number): QueryKey =>
  [...getReleaseGroupBaseQueryKey(releaseGroupId), 'release', releaseId];

/**
 * Refetch a release while any of its projects are still having their dependencies determined.
 */
export const getReleaseRefetchInterval = (
  release: ProjectGroupRelease | undefined,
  query: { state: { status: QueryStatus } },
): number | false => {
  //  After a failed refetch, react-query still passes the last successful release, which would keep polling a
  //  release that can no longer be loaded (e.g. one that was deleted).
  if (query.state.status === 'error') {
    return false;
  }
  const isStillDeterminingDependencies = release?.projects.some((project) => !isDependencyCacheValid(project.revision));
  return isStillDeterminingDependencies ? RELEASE_DEPENDENCY_POLL_INTERVAL_MS : false;
};

function fetchRelease(releaseGroupId: number, releaseId: number) {
  return get<ProjectGroupRelease>(`/api/project_group/${releaseGroupId}/release/${releaseId}`, true);
}

/**
 * Query for a single release, including its projects and their revisions.
 */
export function useReleaseGroupRelease(releaseGroupId: number, releaseId: number | null | undefined) {
  return useQuery<ProjectGroupRelease>({
    queryKey: getReleaseGroupReleaseQueryKey(releaseGroupId, releaseId ?? 0),
    queryFn: () => fetchRelease(releaseGroupId, releaseId ?? 0),
    enabled: !!releaseId,
    staleTime: RELEASE_STALE_TIME_MS,
    refetchInterval: getReleaseRefetchInterval,
  });
}

/**
 * Query for the release currently selected in the release group page's release selector.
 */
export function useSelectedReleaseGroupRelease(releaseGroupId: number) {
  return useReleaseGroupRelease(releaseGroupId, useSelectedReleaseId(releaseGroupId));
}
